import axios from "axios";
import { toast } from "sonner";

const csrfTokens = { user: null, admin: null };
const csrfRequests = { user: null, admin: null };
const MUTATING_METHODS = new Set(["post", "put", "patch", "delete"]);
// Rotas usadas antes de existir sessão: buscar token CSRF nelas falha com 401
// e a requisição nem chega a sair.
const PUBLIC_PATHS = [
  "/auth/login",
  "/auth/register",
  // Cadastro com pagamento: as duas acontecem antes de existir qualquer sessão.
  "/auth/cadastro/checkout",
  "/auth/cadastro/confirmar",
  "/auth/csrf",
  "/admin/login",
  "/auth/password-reset/request",
  "/auth/password-reset/confirm",
];

export const getApiBase = () => {
  const configured = process.env.REACT_APP_BACKEND_URL;
  if (!configured) return "/api";
  return `${configured.replace(/\/$/, "")}/api`;
};

const isAllowedApiOrigin = (url) => {
  if (typeof window === "undefined") return false;
  const requestOrigin = new URL(url, window.location.origin).origin;
  const configuredOrigin = new URL(getApiBase(), window.location.origin).origin;
  // Production is same-origin; CRA development commonly runs on :3000 while
  // the API runs on :8010. Both are trusted API origins, not arbitrary hosts.
  return requestOrigin === window.location.origin || requestOrigin === configuredOrigin;
};

const isPublicOrOAuth = (url) => {
  const base = typeof window === "undefined" ? "http://localhost" : window.location.origin;
  const path = new URL(url, base).pathname;
  return PUBLIC_PATHS.some((item) => path.endsWith(item)) || path.includes("/oauth") || path.endsWith("/gcal/connect");
};

export const getCsrfScope = (url) => {
  const base = typeof window === "undefined" ? "http://localhost" : window.location.origin;
  return new URL(url || "", base).pathname.includes("/admin/") ? "admin" : "user";
};

export const getCsrfEndpoint = (scope = "user") =>
  `${getApiBase()}/${scope === "admin" ? "admin/csrf" : "auth/csrf"}`;

export const getCsrfToken = async (scope = "user") => {
  if (csrfTokens[scope]) return csrfTokens[scope];
  if (!csrfRequests[scope]) {
    csrfRequests[scope] = axios.get(getCsrfEndpoint(scope), { withCredentials: true, skipCsrf: true })
      .then(({ data }) => {
        csrfTokens[scope] = data?.csrf_token || data?.csrfToken || data?.token || null;
        return csrfTokens[scope];
      })
      .finally(() => { csrfRequests[scope] = null; });
  }
  return csrfRequests[scope];
};

export const resetCsrfToken = (scope) => {
  if (scope) csrfTokens[scope] = null;
  else { csrfTokens.user = null; csrfTokens.admin = null; }
};

export const csrfRequestInterceptor = async (config) => {
    const method = (config.method || "get").toLowerCase();
    if (!config.skipCsrf && MUTATING_METHODS.has(method) && isAllowedApiOrigin(config.url || "") && !isPublicOrOAuth(config.url || "")) {
      const scope = getCsrfScope(config.url);
      const token = await getCsrfToken(scope);
      if (token) {
        if (config.headers?.set) config.headers.set("X-CSRF-Token", token);
        else config.headers = { ...(config.headers || {}), "X-CSRF-Token": token };
      }
    }
    return config;
};

export const csrfResponseInterceptor = (error) => {
    if (error.response?.status === 403 && !error.config?.skipCsrf && !isPublicOrOAuth(error.config?.url || "")) {
      resetCsrfToken(getCsrfScope(error.config?.url));
      toast.error("Sua sessão ou permissão precisa ser renovada.", {
        id: "session-permission-renewal",
        description: "Atualize a página e tente novamente.",
      });
    }
    return Promise.reject(error);
};

export const installHttpInterceptors = () => {
  const requestId = axios.interceptors.request.use(csrfRequestInterceptor);
  const responseId = axios.interceptors.response.use((response) => response, csrfResponseInterceptor);
  return () => {
    axios.interceptors.request.eject(requestId);
    axios.interceptors.response.eject(responseId);
  };
};

installHttpInterceptors();
